(() => {
  if (window.KickisFreeShippingProgress) return;

  const elements = new Set();
  let refreshTimer = 0;
  let refreshController = null;

  const localeRoot = () => {
    const root = String(window.Shopify?.routes?.root || '/');
    return root.endsWith('/') ? root : `${root}/`;
  };

  const updateAll = (cart, announce = true) => {
    if (!cart || !Number.isFinite(Number(cart.total_price))) return;
    elements.forEach((element) => element.renderCart(cart, announce));
  };

  const refreshCart = async () => {
    refreshController?.abort();
    refreshController = new AbortController();

    try {
      const response = await fetch(`${localeRoot()}cart.js?_shipping_progress=${Date.now()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: refreshController.signal,
      });
      if (!response.ok) return;
      updateAll(await response.json());
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('Kunde inte uppdatera fraktmätaren.', error);
    }
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshCart, 80);
  };

  class FreeShippingProgress extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-free-shipping-track]');
      this.fill = this.querySelector('[data-free-shipping-fill]');
      this.message = this.querySelector('[data-free-shipping-message]');
      this.live = this.querySelector('[data-free-shipping-live]');
      this.thresholdMinor = Math.max(1, Number(this.dataset.thresholdMinor) || 70000);
      this.currency = this.dataset.currency || 'SEK';
      elements.add(this);
      this.renderCart({
        total_price: Number(this.dataset.cartTotalMinor) || 0,
        item_count: Number(this.dataset.cartItemCount) || 0,
        currency: this.currency,
      }, false);
    }

    disconnectedCallback() {
      elements.delete(this);
    }

    formatMoney(minorValue) {
      const value = Math.max(0, Number(minorValue) || 0) / 100;
      const hasDecimals = Math.round(value * 100) % 100 !== 0;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: hasDecimals ? 2 : 0,
          maximumFractionDigits: hasDecimals ? 2 : 0,
        }).format(value).replace(/\u00a0/g, ' ');
      } catch (_) {
        return `${value.toFixed(hasDecimals ? 2 : 0)} kr`;
      }
    }

    renderCart(cart, announce = true) {
      if (!this.track || !this.fill || !this.message) return;

      const totalMinor = Math.max(0, Number(cart.total_price) || 0);
      const itemCount = Math.max(0, Number(cart.item_count) || 0);
      const clampedMinor = Math.min(totalMinor, this.thresholdMinor);
      const remainingMinor = Math.max(0, this.thresholdMinor - totalMinor);
      const ratio = clampedMinor / this.thresholdMinor;
      const isEmpty = itemCount === 0;
      const isComplete = !isEmpty && remainingMinor === 0;
      const state = isEmpty ? 'empty' : (isComplete ? 'complete' : 'active');
      if (cart.currency) this.currency = cart.currency;
      const text = isEmpty
        ? this.dataset.emptyMessage
        : (isComplete
          ? this.dataset.completeMessage
          : `${this.formatMoney(remainingMinor)} ${this.dataset.remainingText}`);

      this.dataset.state = state;
      this.dataset.cartTotalMinor = String(totalMinor);
      this.dataset.cartItemCount = String(itemCount);
      this.track.hidden = isEmpty;
      this.track.setAttribute('aria-valuenow', String(clampedMinor / 100));
      this.track.setAttribute('aria-valuetext', isComplete
        ? 'Gränsen för fri frakt är uppnådd.'
        : `${this.formatMoney(clampedMinor)} av ${this.formatMoney(this.thresholdMinor)}. ${text}.`);
      this.fill.style.setProperty('--free-shipping-progress', `${Math.min(100, Math.max(0, ratio * 100))}%`);
      this.message.textContent = text;

      if (announce && this.dataset.announceUpdates === 'true' && this.live) {
        this.live.textContent = text;
      }
    }
  }

  window.KickisFreeShippingProgress = FreeShippingProgress;
  customElements.define('free-shipping-progress', FreeShippingProgress);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cartData = event?.cartData;
      if (Number.isFinite(Number(cartData?.total_price)) && Number.isFinite(Number(cartData?.item_count))) {
        updateAll(cartData);
      } else {
        scheduleRefresh();
      }
    });
  }

  document.addEventListener('cart:refresh', scheduleRefresh);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRefresh();
  });
})();
